import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ITicket } from '@shared/interfaces/ticket.interface';
import { GraphsResponse, GraphParams } from '@shared/interfaces/graphs.interface';
import { Observable, catchError, debounceTime, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenService } from './token.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(private httpclient: HttpClient, private tokenService: TokenService, private router: Router) { }

  public post(endpoint: string, data: any) {
    console.log(environment.baseUrl + endpoint);
    return this.httpclient.post(environment.baseUrl + endpoint, data);
  }

  public get(endpoint: string) {
    return this.httpclient.get(environment.baseUrl + endpoint)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getGraphs(params: GraphParams): Observable<GraphsResponse> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('detailed', params.detailed?.toString() || 'false')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    return this.httpclient.get<GraphsResponse>(`${environment.baseUrl}/graphs`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({} as GraphsResponse);
          }
          return throwError(() => error);
        })
      );
  }

  public getOffices(): Observable<any[]> {
    return this.httpclient.get<any[]>(environment.baseUrl + '/office/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getOffice(office: any) {
    return this.httpclient.get(environment.baseUrl + `/office/get/${office}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public setOfficeToUser(office: any) {
    return this.httpclient.get(environment.baseUrl + `/office/set/user/${office}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getUsers() {
    return this.httpclient.get(environment.baseUrl + '/user/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getUser(user: any) {
    return this.httpclient.get(environment.baseUrl + `/office/get/${user}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getUserAuth() {
    return this.httpclient.get(environment.baseUrl + `/user/get/auth`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookings() {
    return this.httpclient.get(environment.baseUrl + '/booking/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingsPaginated(numberPage: number, textToSearch: string) {
    return this.httpclient.get(environment.baseUrl + '/booking/get/paginated?page=' + numberPage + '&text=' + textToSearch)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getTicketsPaginated(numberPage: number, textToSearch: string, statusTicket: number, dateFilter: any, officeId: string | null) {

    return this.httpclient.get(`${environment.baseUrl}/tickets/get/paginated?page=${numberPage}&text=${textToSearch}&ticket_status=${statusTicket}&date_filter=${dateFilter}&office_id=${officeId}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getParticipantsByBooking(reference: string) {
    return this.httpclient.get(`${environment.baseUrl}/booking/participants/get/${reference}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getTicketIdsFromFilters(textToSearch: string, statusTicket: number, dateFilter: any, officeId: string | null) {

    return this.httpclient.get(`${environment.baseUrl}/tickets/get/ids?text=${textToSearch}&ticket_status=${statusTicket}&date_filter=${dateFilter}&office_id=${officeId}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getInvoicesPaginated(numberPage: number, textToSearch: string) {
    console.log('page: ', numberPage);
    return this.httpclient.get(environment.baseUrl + '/invoices/get/paginated?page=' + numberPage + '&text=' + textToSearch)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public saveTicketFromInvoice(invoice: any) {
    return this.httpclient.get(environment.baseUrl + '/tickets/save-from-invoice/' + invoice.id)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getProformasPaginated(numberPage: number, textToSearch: string) {
    return this.httpclient.get(environment.baseUrl + '/proformas/get/paginated?page=' + numberPage + '&text=' + textToSearch)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingsPlanning() {
    return this.httpclient.get(environment.baseUrl + '/booking/planning')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingServicesPlanning() {
    return this.httpclient.get(environment.baseUrl + '/booking-service/planning/')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingServicesByMonth(date: string) {
    return this.httpclient.get(environment.baseUrl + '/booking-service/planning-month/' + date)
      .pipe(
        debounceTime(500),
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingServicesByDay(date: string) {
    return this.httpclient.get(environment.baseUrl + '/booking-service/planning-day/' + date)
      .pipe(
        debounceTime(500),
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingServicesByHour(date: string) {
    return this.httpclient.get(environment.baseUrl + '/booking-service/planning-hour/' + date)
      .pipe(
        debounceTime(500),
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingServicesAllItemsByGroup() {
    return this.httpclient.get(environment.baseUrl + '/booking-service/items-group/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingServicesPlanningToday() {
    return this.httpclient.get(environment.baseUrl + '/booking-service/planning-today')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getAllBookingsPlanning() {
    return this.httpclient.get(environment.baseUrl + '/booking/planning/all')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingCharts() {
    return this.httpclient.get(environment.baseUrl + '/booking/charts')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getTotalSellToday() {
    return this.httpclient.get(environment.baseUrl + '/booking/sell-today/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getTotalSellTurn() {
    return this.httpclient.get(environment.baseUrl + '/booking/sell-turn/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBookingById(booking: any) {
    return this.httpclient.get(environment.baseUrl + `/booking/get/${booking}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getCustomers() {
    return this.httpclient.get(environment.baseUrl + '/customer/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );

  }

  public getCustomersPaginated(textToSearch: string) {
    return this.httpclient.get(environment.baseUrl + '/customer/find/paginated?text=' + textToSearch)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );

  }

  public getCustomerById(id: any) {
    return this.httpclient.get(environment.baseUrl + `/customer/get/${id}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getAddressList() {
    return this.httpclient.get(environment.baseUrl + '/address/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getAddressCustomer(id: any) {
    return this.httpclient.get(environment.baseUrl + `/address/get/${id}`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getItems(): Observable<any[]> {
    return this.httpclient.get<any[]>(environment.baseUrl + '/item/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getBillables(): Observable<any[]> {
    return this.httpclient.get<any[]>(environment.baseUrl + '/billables/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getServicesListAll() {
    return this.httpclient.get(environment.baseUrl + '/service/get-all')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getServicesList() {
    return this.httpclient.get(environment.baseUrl + '/service/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getServicesListWithWeb() {
    return this.httpclient.get(environment.baseUrl + '/service/get-with-web')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getServicesGroupList() {
    return this.httpclient.get(environment.baseUrl + '/service/group/get');
  }

  public getServicesGroupListWithWeb() {
    return this.httpclient.get(environment.baseUrl + '/service/group/get-with-web');
  }

  public getWarehousesByOffice(officeId: number): Observable<any[]> {
    return this.httpclient.get<any[]>(`${environment.baseUrl}/office/get/${officeId}/warehouses`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getItemsByWarehouse(warehouseId: number): Observable<any[]> {
    return this.httpclient.get<any[]>(`${environment.baseUrl}/warehouse/get/${warehouseId}/items`)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getServicesProfileList() {
    return this.httpclient.get(environment.baseUrl + '/serviceProfile/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getAvailability(service_id: number, date: Date) {
    return this.httpclient.post(environment.baseUrl + '/availability/check', { service_id, date })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getItemsAndWarehouses() {
    return this.httpclient.get(environment.baseUrl + '/warehouse/item-warehouse')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getStatus() {
    return this.httpclient.get(environment.baseUrl + '/status')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getTickets() {
    return this.httpclient.get(environment.baseUrl + '/tickets/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getAllTickets() {
    return this.httpclient.get(environment.baseUrl + '/tickets/get-all')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getTicketsDraft() {
    return this.httpclient.get(environment.baseUrl + '/tickets/draft/get')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getTicketById(id: number) {
    return this.httpclient.get(environment.baseUrl + '/tickets/get/' + id)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getInvoiceById(id: number) {
    return this.httpclient.get(environment.baseUrl + '/invoices/get/' + id)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public printTicket(ticket_id: number) {
    return this.httpclient.get(environment.baseUrl + '/tickets/print/' + ticket_id)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public openTicket() {
    return this.httpclient.get(environment.baseUrl + '/tickets/open')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public updateTicket(ticket: ITicket) {
    return this.httpclient.post(environment.baseUrl + '/tickets/update/' + ticket.id, { ...ticket })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }
  public updateFieldsTicket(ticket_id: any, data: any) {
    return this.httpclient.post(environment.baseUrl + '/tickets/update-fields/' + ticket_id, data)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public saveInvoice(invoice: any) {
    return this.httpclient.post(environment.baseUrl + '/invoices/create', { invoice })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public updateInvoice(invoice: any) {
    return this.httpclient.post(environment.baseUrl + '/invoices/update', { invoice })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public recoveryTicket(ticket: number) {
    return this.httpclient.get(environment.baseUrl + '/tickets/recovery/' + ticket)
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public refundTicket(ticket_id: number, refund_comment: string) {
    return this.httpclient.post(environment.baseUrl + '/tickets/refund/' + ticket_id, { refund_comment })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public updateTicketDetails(ticketDetails: any) {
    return this.httpclient.post(environment.baseUrl + '/tickets/details/update', { ticketDetails })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public updateInvoiceDetails(invoiceDetails: any) {
    return this.httpclient.post(environment.baseUrl + '/invoices/details/update', { invoiceDetails })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  public getSettings() {
    return this.httpclient.get(environment.baseUrl + '/settings/get/')
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of();
          }
          return of(error);
        })
      );
  }

  // Nuevos métodos para endpoints detallados de Excel
  public getPaymentMethodsDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    console.log('Llamando a endpoint: /graphs/payment-methods-detailed con params:', queryParams.toString());

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/payment-methods-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          console.error('Error en getPaymentMethodsDetailed:', error);
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

  public getTicketsDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    console.log('Llamando a endpoint: /graphs/tickets-detailed con params:', queryParams.toString());

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/tickets-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          console.error('Error en getTicketsDetailed:', error);
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

  public getInvoicesDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/invoices-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

  public getBookingsCompletedDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/bookings-completed-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

  public getParticipantsDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/participants-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

  public getTotalSalesDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/total-sales-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

  public getSalesConsumedDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/sales-consumed-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

  public getUnitsSoldByServiceGroupDetailed(params: GraphParams): Observable<any> {
    const queryParams = new HttpParams()
      .set('temporal_format', params.temporal_format || 'monthly')
      .set('employee_id', params.employee_id?.toString() || '')
      .set('cashier_id', params.cashier_id?.toString() || '')
      .set('service_group_id', params.service_group_id?.toString() || '')
      .set('payment_method', params.payment_method || '')
      .set('range_dates', params.range_dates?.map(d => d.toISOString().split('T')[0]).join(',') || '');

    console.log('Llamando a endpoint: /graphs/units-sold-by-service-group-detailed con params:', queryParams.toString());

    return this.httpclient.get<any>(`${environment.baseUrl}/graphs/units-sold-by-service-group-detailed`, { params: queryParams })
      .pipe(
        catchError((error: any) => {
          console.error('Error en getUnitsSoldByServiceGroupDetailed:', error);
          if (error.status === 401) {
            this.tokenService.removeToken();
            this.router.navigate(['/login']);
            return of({});
          }
          return throwError(() => error);
        })
      );
  }

}
